import React, {useEffect, useMemo} from "react";
import {useNavigate, Link as RouterLink} from "react-router-dom";
import {PlainDate, TemporalFormat, TemporalRangeFormat} from "@variocube/app-ui";
import { useAsync } from "react-async-hook";
import {
	Alert, Box, Button,
	Card,
	CardHeader, Container,
	Grid,
	Paper,
	Stack,
	Typography
} from "@mui/material";
import {notBlank} from "../utils";
import {useLocalization} from "../i18n";
import {useTenant} from "../context/tenant";
import {useSiteApi} from "../data/sites";
import {AppLayout} from "../component/app-layout";
import { Accessibility } from "../component/accessibility";
import {B2SiteSummary} from "../data/client.gen";

export function SiteList() {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {tenant} = useTenant();
	const {listSites} = useSiteApi();

	const {result: { content: sites } = {}, error, loading} = useAsync(async () =>
		(tenant !== undefined)
			? listSites(tenant.id)
			: undefined
		, [tenant]);

	useEffect(() => {
		if (tenant !== undefined && sites !== undefined && sites.length === 1) {
			navigate(`/${tenant.id}/${sites[0].id}`);
		}
	}, [tenant, sites]);

	return (
		<AppLayout
			showBrandingBar={true}
			gutterTop={true}
			{...{ loading, error }}
		>
			<Container maxWidth="md">
				{sites !== undefined && sites.length === 0 && (
					<Paper elevation={0}>
						<Alert severity="warning" variant="outlined">
							{t('sites.noSites')}
						</Alert>
					</Paper>
				)}

				{sites !== undefined && (
					<Stack
						direction="column"
						gap={2}
					>
						{sites.map((site) => (
							<SiteCard key={site.id} site={site} />
						))}
					</Stack>
				)}
			</Container>
		</AppLayout>
	)
}


function SiteCard({site}: { site: B2SiteSummary }) {
	const {t, language} = useLocalization();
	const {tenant} = useTenant();
    const {id, name, street, city, country, activeFrom, activeUntil} = site;

	const active = useMemo(() => {
		if (notBlank(activeFrom) || notBlank(activeUntil)) {
			if (notBlank(activeFrom) && notBlank(activeUntil) && activeFrom !== activeUntil) {
				return (
					<TemporalRangeFormat
						from={PlainDate.from(activeFrom as string)}
						until={PlainDate.from(activeUntil as string)}
						locale={language}
					/>
				)
			}
			else if (activeFrom === activeUntil) {
				return <TemporalFormat value={PlainDate.from(activeFrom as string)} locale={language} />
			}
		}
		return undefined;
    }, [activeFrom, activeUntil, language]);

    return (
		<Card>
			<CardHeader title={name} subheader={active}/>
			<Grid container spacing={2} sx={{ p: 2 }}>
				<Grid item xs={12} sm={9}>
					<Typography variant="body1" gutterBottom>
						{[street, city, country].join(", ")}
					</Typography>
					<Typography variant="body2" gutterBottom>
						<Accessibility site={site}/>
					</Typography>
				</Grid>
				<Grid item xs={12} sm={3}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: "flex-end"
						}}
					>
						<Button
							color="primary"
							size="large"
							variant="contained"
							component={RouterLink}
							to={`/${tenant?.id}/${id}`}
						>
							{t("actions.book")}
						</Button>
					</Box>
				</Grid>
			</Grid>
		</Card>
    )
}

